import { useState, useEffect, createContext, useContext } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { T, tone } from './theme';
import { RESPONSE_POOR_RATE, accountToneName, bookingToneName, fmtDate, responseSummary, sortByDate } from './lib/format';
import { Badge, BikeThumb, Icon, Lightbox, ProfileField, ProfileSection, ReasonBanner, StatCard, UserAvatar, useBikeThumbs } from './ui';
import { BookingLink } from './bookingViewer';

// Mirrors app/constants/heardFrom.ts; see the note in panels/Analytics.js.
const HEARD_FROM_LABEL = {
  social_media: 'Social media',
  friend: 'Through a friend',
  billboard: 'Billboard',
  other: 'Something else',
};

export const ProfileViewerContext = createContext(null);

export const useProfileViewer = () => useContext(ProfileViewerContext) || (() => {});

// A person's name, rendered as a link when we know their id and as plain text
// when we do not (old bookings predate some of the id fields).

export function UserLink({ userId, name, fallback = 'Unknown', color = T.textSecondary, weight = 600, size = 13 }) {
  const openProfile = useProfileViewer();
  const label = name || fallback;
  if (!userId) {
    return <span style={{ color, fontWeight: weight, fontSize: size }}>{label}</span>;
  }
  return (
    <button
      className="ui-userlink"
      // Rows in Bookings, Bikes and Reports are themselves clickable, so the
      // click must not also open whatever is behind the name.
      onClick={(e) => { e.stopPropagation(); openProfile(userId); }}
      title="View profile"
      style={{
        background: 'none', border: 'none', padding: 0, margin: 0,
        font: 'inherit', fontSize: size, fontWeight: weight, color,
        cursor: 'pointer', textAlign: 'left',
        textDecoration: 'underline', textDecorationStyle: 'dotted',
        textUnderlineOffset: 3, textDecorationColor: 'currentColor',
      }}
    >{label}</button>
  );
}

// ── Bike thumbnails for bookings ──────────────────────────
// A booking stores bikeName but no photo, so the picture has to be resolved
// from the bike doc. Cached at module level and keyed by bike id: several
// bookings usually share one bike, and the same bikes reappear across the
// Bookings tab, booking threads and profile histories, so this settles to zero
// reads after the first look.

export function UserProfileModal({ userId, onClose }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [photoOpen, setPhotoOpen] = useState(false);
  const thumbs = useBikeThumbs(
    data ? [...new Set(data.bookings.map(b => b.bikeId).filter(Boolean))].join(',') : ''
  );

  useEffect(() => {
    // While the enlarged photo is up, Escape belongs to the Lightbox — without
    // this both handlers fire and one key closes the profile as well.
    const onKey = (e) => { if (e.key === 'Escape' && !photoOpen) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, photoOpen]);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    setLoading(true); setError(''); setData(null); setPhotoOpen(false);

    (async () => {
      try {
        // Both sides of every booking are fetched because a person can be the
        // renter on one and the owner on another — the history is the union.
        const [userSnap, privSnap, paySnap, asRenter, asOwner, bikeSnap, ratingSnap] = await Promise.all([
          getDoc(doc(db, 'users', userId)),
          getDoc(doc(db, 'users', userId, 'private', 'identity')).catch(() => null),
          // Bank details moved here alongside the ID number; see payout.js.
          getDoc(doc(db, 'users', userId, 'private', 'payout')).catch(() => null),
          getDocs(query(collection(db, 'bookings'), where('renterId', '==', userId))),
          getDocs(query(collection(db, 'bookings'), where('ownerId',  '==', userId))),
          getDocs(query(collection(db, 'bikes'),    where('ownerId',  '==', userId))),
          getDocs(query(collection(db, 'ratings'),  where('rateeId',  '==', userId))),
        ]);
        if (cancelled) return;

        if (!userSnap.exists()) {
          setError('This account no longer exists.');
          setLoading(false);
          return;
        }

        const byId = new Map();
        [...asRenter.docs, ...asOwner.docs].forEach(d => byId.set(d.id, { id: d.id, ...d.data() }));
        const bookings = sortByDate([...byId.values()], 'createdAt', 'desc');

        setData({
          user: {
            id: userSnap.id,
            ...userSnap.data(),
            ...(privSnap && privSnap.exists() ? privSnap.data() : {}),
            ...(paySnap && paySnap.exists() ? paySnap.data() : {}),
          },
          bookings,
          bikes:   bikeSnap.docs.map(d => ({ id: d.id, ...d.data() })),
          ratings: sortByDate(ratingSnap.docs.map(d => ({ id: d.id, ...d.data() })), 'createdAt', 'desc'),
        });
        setLoading(false);
      } catch (e) {
        console.error(e);
        if (cancelled) return;
        setError('Could not load this profile.');
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [userId]);

  if (!userId) return null;

  const user   = data?.user;
  const accent = user?.role === 'owner' ? T.owner : T.primary;
  const accentDim = user?.role === 'owner' ? T.ownerDim : T.primaryDim;

  const asRenterBookings = data ? data.bookings.filter(b => b.renterId === userId) : [];
  const asOwnerBookings  = data ? data.bookings.filter(b => b.ownerId  === userId) : [];
  const completed = data ? data.bookings.filter(b => b.status === 'completed') : [];
  const spent  = asRenterBookings.filter(b => b.status === 'completed').reduce((s, b) => s + (Number(b.total) || 0), 0);
  const earned = asOwnerBookings.filter(b => b.status === 'completed').reduce((s, b) => s + (Number(b.total) || 0), 0);
  const scored = data ? data.ratings.filter(r => Number(r.overallRating) > 0) : [];
  const avgRating = scored.length
    ? (scored.reduce((s, r) => s + Number(r.overallRating), 0) / scored.length).toFixed(1)
    : null;
  const response = user ? responseSummary(user) : null;

  return (
    <>
    <div className="ui-overlay" style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(9, 12, 18, 0.6)',
      backdropFilter: 'blur(3px)', zIndex: 300,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: 20, overflowY: 'auto',
    }} onClick={onClose}>
      <div
        className="ui-modal"
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: T.card, border: `1px solid ${T.cardBorder}`,
          borderRadius: 16, padding: 24, width: '100%', maxWidth: 780,
          boxShadow: T.shadowLg, margin: 'auto',
        }}
      >
        {/* Close */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: -8 }}>
          <button onClick={onClose} className="ui-btn" title="Close" style={{
            background: 'none', border: `1px solid ${T.cardBorder}`, borderRadius: 8,
            padding: '5px 8px', cursor: 'pointer', display: 'inline-flex',
          }}>
            <Icon name="x" size={15} color={T.textMuted} />
          </button>
        </div>

        {loading ? (
          <div style={{ color: T.textMuted, padding: '40px 0', textAlign: 'center' }}>Loading profile…</div>
        ) : error ? (
          <div style={{ color: T.danger, padding: '40px 0', textAlign: 'center' }}>{error}</div>
        ) : (
          <>
            {/* ── Header ── */}
            <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
              <div
                onClick={() => user.photoURL && setPhotoOpen(true)}
                style={{ cursor: user.photoURL ? 'zoom-in' : 'default', flexShrink: 0 }}
                title={user.photoURL ? 'Click to enlarge' : undefined}
              >
                <UserAvatar user={user} color={accent} bg={accentDim} size={96} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ color: T.textPrimary, fontSize: 22, fontWeight: 800, letterSpacing: -0.4 }}>
                  {user.name || 'Unnamed'}
                </div>
                <div style={{ color: T.textMuted, fontSize: 14, marginTop: 3 }}>+960 {user.phone}</div>
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 10 }}>
                  <Badge
                    label={user.role === 'owner' ? 'Owner' : 'Renter'}
                    color={accent}
                    bg={accentDim}
                    border={user.role === 'owner' ? T.ownerBorder : T.primaryBorder}
                  />
                  <Badge label={user.status || 'unknown'} {...tone(accountToneName(user.status))} />
                  {user.photoLocked && <Badge label="Photo verified" {...tone('success')} />}
                  {avgRating && <Badge label={`★ ${avgRating} (${scored.length})`} {...tone('primary')} />}
                </div>
              </div>
            </div>

            {/* Why an account is not currently good standing */}
            {user.status === 'rejected' && (
              <ReasonBanner title="Rejected." reason={user.rejectionReason} when={user.rejectionAt} />
            )}
            {user.status === 'pending' && user.revocationReason && (
              <ReasonBanner title="Previously revoked." reason={user.revocationReason} when={user.revokedAt} tone="warning" />
            )}

            {/* ── Numbers ── */}
            <div style={{
              display: 'grid', gap: 10, marginTop: 20,
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            }}>
              <StatCard label="Bookings" value={data.bookings.length} toneName="primary" />
              <StatCard label="Completed" value={completed.length} toneName="success" />
              {asRenterBookings.length > 0 && (
                <StatCard label="Spent as renter" value={`MVR ${spent.toLocaleString()}`} toneName="primary" />
              )}
              {asOwnerBookings.length > 0 && (
                <StatCard label="Earned as owner" value={`MVR ${earned.toLocaleString()}`} toneName="warning" />
              )}
              {response && (
                <StatCard
                  label="Replied to requests"
                  value={`${response.pct}%`}
                  sub={`${response.answered}/${response.received}${response.avgMinutes != null ? ` · ~${response.avgMinutes} min` : ''}`}
                  toneName={!response.enough ? 'muted' : response.rate >= 0.8 ? 'success' : response.rate >= RESPONSE_POOR_RATE ? 'warning' : 'danger'}
                />
              )}
            </div>

            {/* ── Details ── */}
            <ProfileSection title="Details">
              <div style={{
                backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`,
                borderRadius: 12, padding: 16,
                display: 'grid', gap: 16,
                gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
              }}>
                <ProfileField label="ID number">
                  {user.nidNumber || <span style={{ color: T.danger }}>Not provided</span>}
                </ProfileField>
                <ProfileField label="Email">{user.email || '—'}</ProfileField>
                {user.heardFrom && (
                  <ProfileField label="Heard about us">
                    {HEARD_FROM_LABEL[user.heardFrom] || user.heardFrom}
                    {user.heardFromOther ? (
                      <><br /><span style={{ color: T.textMuted }}>{user.heardFromOther}</span></>
                    ) : null}
                  </ProfileField>
                )}
                <ProfileField label="Joined">{fmtDate(user.createdAt)}</ProfileField>
                <ProfileField label="Verification document">
                  {user.licenseFile ? (
                    <a href={user.licenseFile} target="_blank" rel="noreferrer" style={{
                      color: T.primary, fontWeight: 700, textDecoration: 'none',
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                    }}>
                      <Icon name="external" size={13} color={T.primary} />
                      {user.verificationDocType === 'nid' ? 'ID card' : 'License'}
                    </a>
                  ) : <span style={{ color: T.textMuted }}>None on file</span>}
                </ProfileField>
                {user.agreedToTermsAt && (
                  <ProfileField label="Agreed to terms">{fmtDate(user.agreedToTermsAt)}</ProfileField>
                )}
                {user.bankName && (
                  <ProfileField label="Bank">
                    {user.bankName}<br />
                    <span style={{ color: T.textSecondary }}>{user.accountName}</span><br />
                    <span style={{ color: T.textMuted }}>{user.accountNumber}</span>
                  </ProfileField>
                )}
                {user.referralCode && (
                  <ProfileField label="Referral code">
                    {user.referralCode}
                    {user.referralTier ? <span style={{ color: T.textMuted }}> · {user.referralTier}</span> : null}
                  </ProfileField>
                )}
              </div>
            </ProfileSection>

            {/* ── Listed bikes ── */}
            {data.bikes.length > 0 && (
              <ProfileSection title="Listed bikes" count={data.bikes.length}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.bikes.map(b => (
                    <div key={b.id} style={{
                      backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`,
                      borderRadius: 10, padding: 10,
                      display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
                    }}>
                      {b.photos?.[0] && (
                        <img src={b.photos[0]} alt={b.model} style={{
                          width: 64, height: 44, objectFit: 'cover', borderRadius: 7,
                          border: `1px solid ${T.cardBorder}`, flexShrink: 0,
                        }} />
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: T.textPrimary, fontWeight: 700, fontSize: 13.5 }}>{b.model || 'Bike'}</div>
                        <div style={{ color: T.textMuted, fontSize: 12 }}>
                          {b.plate ? `${b.plate} · ` : ''}MVR {b.pricePerDay}/day
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <Badge label={b.status || 'unknown'} {...tone(b.status === 'verified' ? 'success' : b.status === 'rejected' ? 'danger' : 'warning')} />
                        {b.isActive === false && <Badge label="Switched off" {...tone('muted')} />}
                        {b.currentlyBooked && <Badge label="Booked" {...tone('warning')} />}
                        {b.ownerVerified !== true && <Badge label="Owner unverified" {...tone('danger')} />}
                      </div>
                    </div>
                  ))}
                </div>
              </ProfileSection>
            )}

            {/* ── Rental history ── */}
            <ProfileSection title="Rental history" count={data.bookings.length}>
              {data.bookings.length === 0 ? (
                <div style={{ color: T.textMuted, fontSize: 13, padding: '10px 2px' }}>No bookings yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.bookings.map(b => {
                    const asRenter = b.renterId === userId;
                    const otherId   = asRenter ? b.ownerId   : b.renterId;
                    const otherName = asRenter ? b.ownerName : b.renterName;
                    return (
                      <div key={b.id} style={{
                        backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`,
                        borderRadius: 10, padding: 11,
                        display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
                      }}>
                        <BikeThumb src={thumbs.get(b.bikeId) || ''} size={46} />
                        <div style={{ flex: 1, minWidth: 160 }}>
                          {b.bookingRef && (
                            <div><BookingLink bookingId={b.id} label={b.bookingRef} /></div>
                          )}
                          <div style={{ color: T.textPrimary, fontWeight: 700, fontSize: 13.5 }}>{b.bikeName || 'Bike'}</div>
                          <div style={{ color: T.textMuted, fontSize: 12, marginTop: 2 }}>
                            {asRenter ? 'Rented from ' : 'Rented to '}
                            <UserLink userId={otherId} name={otherName} size={12} color={asRenter ? T.owner : T.textSecondary} />
                          </div>
                        </div>
                        <div style={{ color: T.textSecondary, fontSize: 12, whiteSpace: 'nowrap' }}>
                          {b.startDate} → {b.endDate}
                        </div>
                        <div style={{ color: T.primary, fontSize: 13.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
                          MVR {b.total}
                        </div>
                        <Badge label={b.status?.replace(/_/g, ' ') || 'unknown'} {...tone(bookingToneName(b.status))} />
                      </div>
                    );
                  })}
                </div>
              )}
            </ProfileSection>

            {/* ── Reviews received ── */}
            {data.ratings.length > 0 && (
              <ProfileSection title="Reviews received" count={data.ratings.length}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.ratings.map(r => (
                    <div key={r.id} style={{
                      backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`,
                      borderRadius: 10, padding: 11, opacity: r.hidden ? 0.55 : 1,
                    }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ color: T.primary, fontWeight: 700, fontSize: 13 }}>
                          {'★'.repeat(Math.round(Number(r.overallRating) || 0)) || '—'}
                        </span>
                        <span style={{ color: T.textMuted, fontSize: 12 }}>
                          {/* raterDisplayName is the censored name renters see in
                              the app; admin gets the real one behind the link. */}
                          <UserLink userId={r.raterId} name={r.raterDisplayName || 'Someone'} size={12} color={T.textMuted} weight={600} />
                          {' · '}{fmtDate(r.createdAt)}
                        </span>
                        {r.hidden && <Badge label="Hidden" {...tone('danger')} />}
                      </div>
                      {r.review && (
                        <div style={{ color: T.textSecondary, fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>{r.review}</div>
                      )}
                    </div>
                  ))}
                </div>
              </ProfileSection>
            )}
          </>
        )}
      </div>
    </div>

    {/* Outside the overlay so a click inside it cannot bubble up and close the
        profile underneath. */}
    {photoOpen && user?.photoURL && (
      <Lightbox photos={[user.photoURL]} index={0} onIndex={() => {}} onClose={() => setPhotoOpen(false)} />
    )}
    </>
  );
}
